import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { ChevronRight, CheckCircle2, Lock, Shield, AlertCircle, Upload, MapPin, Wrench, Zap, Home, Building2, FileText, Award, Truck, Users, DollarSign } from 'lucide-react';

interface ServiceCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  proficiency: 'Beginner' | 'Experienced' | 'Expert' | '';
  specializations: string[];
}

interface ProviderFormData {
  // Step 1: Basic Details
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  tradingName: string;
  companyRegNumber: string;
  vatNumber: string;
  
  // Step 2: Service Capabilities
  services: ServiceCategory[];
  
  // Step 3: Coverage & Preferences
  postcodes: string[];
  customerPreference: 'residential' | 'commercial' | 'both' | '';
  vehicleType: string;
  teamSize: 'individual' | 'squad' | 'enterprise' | '';
  jobValueMin: string;
  jobValueMax: string;
  qualityRating: string;
  
  // Step 4: Compliance Setup
  complianceStatus: string;
  insuranceExpiryDate: string;
  documentIdFile: File | null;
  insuranceFile: File | null;
}

const AddProviderPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ProviderFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    tradingName: '',
    companyRegNumber: '',
    vatNumber: '',
    services: [
      { id: 'plumbing', name: 'Plumbing', icon: <Wrench className="w-6 h-6" />, proficiency: '', specializations: [] },
      { id: 'electrical', name: 'Electrical', icon: <Zap className="w-6 h-6" />, proficiency: '', specializations: [] },
      { id: 'hvac', name: 'HVAC', icon: <Home className="w-6 h-6" />, proficiency: '', specializations: [] },
      { id: 'cleaning', name: 'Cleaning', icon: <Building2 className="w-6 h-6" />, proficiency: '', specializations: [] },
    ],
    postcodes: [],
    customerPreference: '',
    vehicleType: '',
    teamSize: '',
    jobValueMin: '',
    jobValueMax: '',
    qualityRating: '4.2',
    complianceStatus: 'Pending Verification',
    insuranceExpiryDate: '',
    documentIdFile: null,
    insuranceFile: null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as any;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSelectChange = (name: string, values: string[]) => {
    setFormData({
      ...formData,
      [name]: values,
    });
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email && 
                 formData.phoneNumber && formData.tradingName && formData.companyRegNumber);
      case 2:
        return formData.services.some(s => s.proficiency);
      case 3:
        return formData.postcodes.length > 0 || true; // Allow next if postcode logic is set
      case 4:
        return !!formData.complianceStatus && !!formData.insuranceExpiryDate;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (validateStep(4)) {
      try {
        const payload = {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phoneNumber,
          trading_name: formData.tradingName,
          company_reg_number: formData.companyRegNumber,
          vat_number: formData.vatNumber,
          services: JSON.stringify(formData.services.filter(s => s.proficiency)),
          postcodes: JSON.stringify(formData.postcodes),
          customer_preference: formData.customerPreference,
          vehicle_type: formData.vehicleType,
          team_size: formData.teamSize,
          job_value_min: formData.jobValueMin,
          job_value_max: formData.jobValueMax,
          compliance_status: formData.complianceStatus,
          insurance_expiry_date: formData.insuranceExpiryDate,
          status: 'ACTIVE',
          rating: 4.0
        };

        console.log('Submitting provider data:', payload);
        
        const response = await apiService.request('/admin/providers', 'POST', payload);
        
        if (response.success) {
          alert('✓ Provider onboarded successfully!');
          navigate('/admin/providers');
        } else {
          alert('Error: ' + (response.message || 'Failed to create provider'));
        }
      } catch (error: any) {
        console.error('Error submitting form:', error);
        alert('Error submitting form: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const steps = [
    { number: 1, title: 'Basic Details', description: 'Personal & Business Information' },
    { number: 2, title: 'Service Capabilities', description: 'Services & Certifications' },
    { number: 3, title: 'Coverage & Preferences', description: 'Service Areas & Hours' },
    { number: 4, title: 'Compliance Setup', description: 'Insurance & Verification' },
  ];

  return (
    <>
      <Helmet>
        <title>Add New Provider - Virewire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/admin/providers')}
            className="mb-8 flex items-center gap-2 text-gray-600 hover:text-gray-900 font-bold text-sm transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Providers
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Add New Provider</h1>
            <p className="text-gray-600">Set up a new service partner account</p>
          </div>

          {/* Step Indicator */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, idx) => (
                <div key={step.number} className="flex-1 flex items-center">
                  <div className="flex items-center gap-4 flex-1">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                        currentStep === step.number
                          ? 'bg-teal-600 text-white ring-4 ring-teal-200'
                          : currentStep > step.number
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {currentStep > step.number ? (
                        <CheckCircle2 className="w-8 h-8" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{step.title}</p>
                      <p className="text-xs text-gray-600">{step.description}</p>
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-12 h-1 mx-4 rounded-full transition-all ${
                      currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            {/* Step 1: Basic Details */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Shield className="w-6 h-6 text-teal-600" />
                    Personal Information
                  </h2>
                  <p className="text-gray-600">Enter the service partner's contact details</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="e.g. John"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="e.g. Smith"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john.smith@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      <select className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors">
                        <option>+44</option>
                        <option>+1</option>
                        <option>+33</option>
                      </select>
                      <input
                        type="tel"
                        name="phoneNumber"
                        placeholder="0712 3456789"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Lock className="w-6 h-6 text-teal-600" />
                    Business Details
                  </h2>
                  <p className="text-gray-600">Enter the business registration details</p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                    Trading Name
                  </label>
                  <input
                    type="text"
                    name="tradingName"
                    placeholder="Official Business Entity Name"
                    value={formData.tradingName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Company Reg Number <span className="text-xs text-gray-500">Optional</span>
                    </label>
                    <input
                      type="text"
                      name="companyRegNumber"
                      placeholder="CRN-12345678"
                      value={formData.companyRegNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">
                      VAT Number <span className="text-xs text-gray-500">Optional</span>
                    </label>
                    <input
                      type="text"
                      name="vatNumber"
                      placeholder="GB123456789"
                      value={formData.vatNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 hover:bg-white transition-colors"
                    />
                  </div>
                </div>

                {/* Compliance Tips */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                    <div className="flex gap-3">
                      <AlertCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-teal-900 mb-1">COMPLIANCE TIP</h3>
                        <p className="text-sm text-teal-800">Verification is automated. Entering accurate Registration Numbers will speed up the KYB process in Step 4.</p>
                        <a href="#" className="text-sm font-bold text-teal-600 hover:text-teal-700 mt-2 inline-block">
                          Learn more about onboarding →
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-12 h-12 text-green-600 mx-auto mb-2" />
                      <p className="font-bold text-green-900 mb-1">Data encrypted & secure</p>
                      <p className="text-sm text-green-800">All information is encrypted using industry-standard 256-bit SSL</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Service Capabilities */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-6">Service Categories</h2>
                  <p className="text-gray-600 text-sm mb-6">Select services and set proficiency level</p>
                  
                  <div className="grid grid-cols-4 gap-4 mb-12">
                    {formData.services.map((service) => (
                      <div
                        key={service.id}
                        onClick={() => {
                          const updatedServices = formData.services.map(s =>
                            s.id === service.id ? { ...s, proficiency: (s.proficiency ? '' : 'Experienced') as 'Beginner' | 'Experienced' | 'Expert' | '' } : s
                          );
                          setFormData({ ...formData, services: updatedServices });
                        }}
                        className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                          service.proficiency
                            ? 'border-teal-600 bg-teal-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="text-teal-600">{service.icon}</div>
                          {service.proficiency && (
                            <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <p className="font-bold text-gray-900 mb-1">{service.name}</p>
                        {service.proficiency && (
                          <div>
                            <label className="text-xs font-bold text-gray-600 uppercase tracking-widest block mb-2">Proficiency</label>
                            <select
                              onClick={(e) => e.stopPropagation()}
                              value={service.proficiency}
                              onChange={(e) => {
                                const updatedServices = formData.services.map(s =>
                                  s.id === service.id ? { ...s, proficiency: e.target.value as any } : s
                                );
                                setFormData({ ...formData, services: updatedServices });
                              }}
                              className="w-full text-xs py-2 px-2 border border-teal-200 rounded text-teal-700 font-bold bg-white focus:outline-none"
                            >
                              <option value="Beginner">Beginner</option>
                              <option value="Experienced">Experienced</option>
                              <option value="Expert">Expert</option>
                            </select>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <h2 className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Technical Skills
                  </h2>
                  <p className="text-gray-600 text-sm mb-6">Refine services based on selected categories</p>

                  <div className="grid grid-cols-2 gap-8">
                    {/* Plumbing Specializations */}
                    {formData.services.some(s => s.id === 'plumbing' && s.proficiency) && (
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-teal-600" />
                          PLUMBING SPECIALIZATIONS
                        </h3>
                        <div className="space-y-3">
                          {['Emergency Leak Repair', 'Gas Line Installation', 'Water Heater Maintenance', 'Septic System Pumping'].map((spec) => (
                            <label key={spec} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                defaultChecked={spec === 'Emergency Leak Repair' || spec === 'Gas Line Installation'}
                                className="w-5 h-5 text-teal-600 rounded cursor-pointer"
                              />
                              <span className="font-bold text-gray-700">{spec}</span>
                              {(spec === 'Emergency Leak Repair') && <span className="ml-auto text-xs font-bold text-teal-600 bg-teal-50 px-2 py-1 rounded">HIGH DEMAND</span>}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cleaning Specializations */}
                    {formData.services.some(s => s.id === 'cleaning' && s.proficiency) && (
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-teal-600" />
                          CLEANING SPECIALIZATIONS
                        </h3>
                        <div className="space-y-3">
                          {['Deep Commercial Sanitization', 'Post-Construction Cleanup', 'Industrial Hazard Cleaning', 'Floor Strip & Wax'].map((spec, idx) => (
                            <label key={spec} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                defaultChecked={idx === 0 || idx === 2}
                                className="w-5 h-5 text-teal-600 rounded cursor-pointer"
                              />
                              <span className="font-bold text-gray-700">{spec}</span>
                              {(idx === 2) && <span className="ml-auto text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">CERT REQUIRED</span>}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Coverage & Preferences */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="grid grid-cols-2 gap-8">
                  {/* Service Coverage */}
                  <div>
                    <h2 className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Service Coverage
                    </h2>
                    <p className="text-gray-600 text-sm mb-6">Geospatial Mapping</p>

                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest block mb-3">Active Postcodes</label>
                      <div className="flex gap-2 flex-wrap mb-4">
                        {['SW1A', 'W1K', 'E1 6AN'].map((pc) => (
                          <span key={pc} className="bg-teal-600 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-2">
                            {pc}
                            <button className="hover:opacity-75">×</button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="text"
                        placeholder="Add postcode..."
                        value={formData.vehicleType}
                        onChange={handleInputChange}
                        name="vehicleType"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 text-sm"
                      />
                      <p className="text-xs text-gray-600 mt-2">Travel radius is calculated from the primary business hub located in central London.</p>
                    </div>

                    {/* Geospatial Map Placeholder */}
                    <div className="mt-6 bg-gradient-to-br from-teal-700 to-teal-900 rounded-xl h-40 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 opacity-20">
                        <svg viewBox="0 0 100 100" className="w-full h-full">
                          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" className="text-teal-400" strokeWidth="0.5" />
                          <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" className="text-teal-300" strokeWidth="0.5" />
                          <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" className="text-teal-200" strokeWidth="0.5" />
                          <circle cx="50" cy="50" r="2" fill="currentColor" className="text-white" />
                        </svg>
                      </div>
                      <div className="bg-white text-teal-700 font-bold text-xs px-3 py-1 rounded-full relative z-10 flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        3 ACTIVE ZONES
                      </div>
                    </div>
                  </div>

                  {/* Customer Preferences */}
                  <div>
                    <h2 className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Customer Preferences
                    </h2>
                    <p className="text-gray-600 text-sm mb-6">Target Market</p>

                    <div className="space-y-4">
                      {[
                        { value: 'residential', icon: Home, label: 'Residential', description: 'Focus on single-family homes, apartments, and private tenancies.' },
                        { value: 'commercial', icon: Building2, label: 'Commercial', description: 'Offices, retail spaces, and corporate facility management.', selected: true },
                        { value: 'both', icon: null, label: 'Both / Unified', description: 'No preference—capable of handling mixed portfolios.' },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            option.selected
                              ? 'border-teal-600 bg-teal-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="customerPreference"
                            value={option.value}
                            checked={formData.customerPreference === option.value || option.selected}
                            onChange={(e) => setFormData({ ...formData, customerPreference: e.target.value as any })}
                            className="w-5 h-5 mt-1 cursor-pointer"
                          />
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">{option.label}</p>
                            <p className="text-xs text-gray-600">{option.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Quality Assurance Profile */}
                    <div className="mt-8 bg-teal-50 border border-teal-200 rounded-xl p-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <Award className="w-4 h-4 text-teal-600" />
                        Quality Assurance Profile
                      </p>
                      <p className="text-xs text-gray-700">Providers must maintain a minimum 4.2 star rating to remain eligible for Commercial contracts.</p>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* Operational Details */}
                <div>
                  <h2 className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-6 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Operational Details
                  </h2>
                  <p className="text-gray-600 text-sm mb-6">Capacity & Logistics</p>

                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Vehicle Type</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 font-bold text-sm">
                        <option>Large Transit Van</option>
                        <option>Small Van</option>
                        <option>Car</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Team Size</label>
                      <div className="space-y-2">
                        {[
                          { value: 'individual', label: 'Individual Specialist (1)' },
                          { value: 'squad', label: 'Standard Squad (2-4)', selected: true },
                          { value: 'enterprise', label: 'Enterprise Crew (5+)' },
                        ].map((option) => (
                          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="teamSize"
                              value={option.value}
                              checked={option.selected}
                              onChange={(e) => setFormData({ ...formData, teamSize: e.target.value as any })}
                              className="w-4 h-4 cursor-pointer"
                            />
                            <span className={`text-sm ${option.selected ? 'font-bold text-teal-700' : 'text-gray-700'}`}>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Preferred Job Value</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          name="jobValueMin"
                          placeholder="£500"
                          value={formData.jobValueMin}
                          onChange={handleInputChange}
                          className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 text-sm"
                        />
                        <span className="text-gray-600 font-bold">to</span>
                        <input
                          type="text"
                          name="jobValueMax"
                          placeholder="£2,500"
                          value={formData.jobValueMax}
                          onChange={handleInputChange}
                          className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Compliance Setup */}
            {currentStep === 4 && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Compliance Setup</h2>
                  <p className="text-gray-600">Establish regulatory standing and verify critical documentation for marketplace eligibility.</p>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  {/* Initial Compliance Standing */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-teal-600" />
                      INITIAL COMPLIANCE STANDING
                    </h3>

                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Current Status</label>
                      <select
                        value={formData.complianceStatus}
                        onChange={(e) => setFormData({ ...formData, complianceStatus: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 font-bold text-sm mb-4"
                      >
                        <option>Pending Verification</option>
                        <option>Under Review</option>
                        <option>Approved</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Insurance Expiry Date</label>
                      <input
                        type="date"
                        value={formData.insuranceExpiryDate}
                        onChange={(e) => setFormData({ ...formData, insuranceExpiryDate: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 bg-gray-50 font-bold text-sm"
                      />
                    </div>

                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-800">
                          <span className="font-bold">Account will be marked as **Pending**</span> until documents are verified by the platform team.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Document Upload */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-600" />
                      DOCUMENT UPLOAD
                    </h3>

                    {/* ID Verification Upload */}
                    <label className="border-2 border-dashed border-gray-300 rounded-xl p-8 mb-6 text-center hover:border-teal-600 hover:bg-teal-50 transition-all cursor-pointer group block">
                      <Upload className="w-10 h-10 text-gray-400 group-hover:text-teal-600 mx-auto mb-3" />
                      <p className="font-bold text-gray-900 mb-1">ID Verification</p>
                      <p className="text-xs text-gray-600 mb-4">Passport, Driver's License or National ID (PDF, JPG)</p>
                      <span className="text-teal-600 hover:text-teal-700 font-bold text-sm bg-white border border-teal-600 px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors inline-block">
                        Browse Files
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setFormData({ ...formData, idDocument: f } as any);
                        }}
                      />
                      {(formData as any).idDocument && (
                        <p className="mt-3 text-xs text-teal-700 font-semibold">
                          Selected: {(formData as any).idDocument.name}
                        </p>
                      )}
                    </label>

                    {/* Insurance Certificate Upload */}
                    <label className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-teal-600 hover:bg-teal-50 transition-all cursor-pointer group block">
                      <Shield className="w-10 h-10 text-gray-400 group-hover:text-teal-600 mx-auto mb-3" />
                      <p className="font-bold text-gray-900 mb-1">Public Liability Insurance</p>
                      <p className="text-xs text-gray-600 mb-4">Certificate of Currency (Min £5M coverage)</p>
                      <span className="text-teal-600 hover:text-teal-700 font-bold text-sm bg-white border border-teal-600 px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors inline-block">
                        Browse Files
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setFormData({ ...formData, insuranceDocument: f } as any);
                        }}
                      />
                      {(formData as any).insuranceDocument && (
                        <p className="mt-3 text-xs text-teal-700 font-semibold">
                          Selected: {(formData as any).insuranceDocument.name}
                        </p>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-12 flex items-center justify-between border-t border-gray-200 pt-8">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-6 py-3 font-bold text-sm transition-colors rounded-lg ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back
              </button>

              {currentStep < 4 ? (
                <div className="flex gap-4">
                  <button
                    onClick={() => navigate('/admin/providers')}
                    className="px-6 py-3 font-bold text-sm text-gray-600 hover:bg-gray-100 transition-colors rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className={`px-8 py-3 font-bold text-sm flex items-center gap-2 rounded-lg transition-colors ${
                      !validateStep(currentStep)
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    Next: {steps[currentStep].title}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-4">
                  <button
                    onClick={() => alert('Draft saved locally')}
                    className="px-8 py-3 font-bold text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-lg border border-gray-300"
                  >
                    Save Progress
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-8 py-3 font-bold text-sm flex items-center gap-2 rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Save & Create Provider
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AddProviderPage;
